import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { nzDate } from '@promanage/core';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { IssueInvoiceForm } from '../features/tenancyInvoices/IssueInvoiceForm';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/bills/invoices/new')({
  component: NewTenancyInvoicePage,
});

/**
 * Raising a tenancy invoice — a charge to a tenant that is not rent.
 *
 * THE LIST IS NOT HERE. It is the Tenancy invoices tab on Bills, beside the supplier bills these
 * recover (see `BillsNav`); this page used to carry a second, hand-built copy of it, and two lists
 * of one set of invoices is two places for them to disagree. What is left is the form, and on
 * success the new invoice's own page — the same shape as `/bills/new`.
 *
 * UNDER /bills, with the list it belongs to, so the sidebar says where the operator is. It used to
 * live under /financial on the argument that owner-or-agency is a trust question — but raising
 * one moves no money, and a page that lights a different section from the list it was opened
 * from reads as having gone somewhere else. `/financial/invoices/new` redirects here.
 *
 * RAISING ONE POSTS NOTHING. The money moves when the tenant pays, as a receipt on `/financial`.
 */
function NewTenancyInvoicePage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const today = nzDate(new Date());

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New tenancy invoice"
        subtitle="A charge to a tenant that is not rent"
        actions={
          <Link to="/bills" search={{ tab: 'tenancy' }}>
            <Button size="sm" variant="secondary">
              Cancel
            </Button>
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="contents" onChange={unsaved.touch}>
          <IssueInvoiceForm
            today={today}
            onIssued={(issued) => {
              unsaved.saved();
              void queryClient.invalidateQueries({
                queryKey: trpc.tenancyInvoices.list.queryKey(),
              });
              void navigate({ to: '/bills/invoices/$ref', params: { ref: issued.ref } });
            }}
          />
        </div>

        <Panel className="p-5">
          <PanelHeader title="What these are" className="p-0 pb-3" />
          <p className="text-[0.8125rem] text-ink-muted">
            A charge to a tenant for something that is not rent — the water bill the owner paid on
            their behalf, a lease break fee, a carpet clean. It credits the owner, who is out of
            pocket, or the agency, which earned it.
          </p>
          <p className="mt-2 text-[0.8125rem] text-ink-muted">
            Raising one moves no money. The tenant’s payment does, receipted on the Financial page.
          </p>
          <p className="mt-2 text-[0.8125rem] text-ink-muted">
            It is <span className="font-medium">not rent arrears</span> and is never added to them —
            a 14-day notice is about rent.
          </p>
        </Panel>
      </div>
    </div>
  );
}
